import { useState } from 'react';
import { INTEREST_TAGS, MAX_INTEREST_TAGS, checkNicknamePolicy } from '@lessalone/shared';
import { CheckinForm } from '../components/CheckinForm';
import { createMembership, recordConsent, saveInterestTags, type MeResponse } from '../lib/api';

type Step = 'consent' | 'nickname' | 'interests' | 'checkin';

const STEP_ORDER: Step[] = ['consent', 'nickname', 'interests', 'checkin'];

/** The points the user must each actively acknowledge before continuing. */
const CONSENT_POINTS = [
  'LessAlone is for connection through text. It is not therapy, not counseling, and not a crisis service.',
  'Conversations are not monitored in real time. In an emergency I will call or text 988, or call 911.',
  'I will be talking with people I don’t know. My nickname is shown to them; my real name and email are never shown.',
  'I am 18 years of age or older.',
];

/**
 * Inspiration chips for the nickname step. A blank textbox is the
 * biggest single drop-off in any onboarding flow — most people can't
 * generate a good pseudonym from cold. A few warm, nature-flavoured
 * suggestions seed the creative direction without prescribing one.
 * The user can pick a suggestion or type their own.
 */
const NICKNAME_SUGGESTIONS = [
  'QuietGrove',
  'WarmRain',
  'PaperLanterns',
  'NorthStar',
  'AmberFox',
  'LinenCloud',
  'GoldenHour',
  'WillowMint',
];

/** Small "Step 2 of 4 · About you" indicator shown above each step's
 *  heading so the user knows how much is ahead. Drop-off goes way down
 *  when the path is finite and visible. */
function StepIndicator({ step, communityName }: { step: Step; communityName: string }) {
  const index = STEP_ORDER.indexOf(step);
  const label =
    step === 'consent'
      ? 'A few promises'
      : step === 'nickname'
        ? 'Your nickname'
        : step === 'interests'
          ? 'What you like'
          : 'A quick check-in';
  return (
    <div className="onboarding-progress">
      <p className="step-label">Joining {communityName}</p>
      <p className="onboarding-step-count">
        Step {index + 1} of {STEP_ORDER.length} · {label}
      </p>
      <div className="onboarding-progress-bar" aria-hidden="true">
        {STEP_ORDER.map((s, i) => (
          <span key={s} className={`onboarding-progress-pip${i <= index ? ' on' : ''}`} />
        ))}
      </div>
    </div>
  );
}

export function Onboarding({ me, onComplete }: { me: MeResponse; onComplete: () => void }) {
  const [step, setStep] = useState<Step>('consent');
  const [checked, setChecked] = useState<boolean[]>(() => CONSENT_POINTS.map(() => false));
  const [nickname, setNickname] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagQuery, setTagQuery] = useState('');
  const [tagError, setTagError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleTag(tag: string) {
    setTags((prev) =>
      prev.includes(tag)
        ? prev.filter((t) => t !== tag)
        : prev.length >= MAX_INTEREST_TAGS
          ? prev
          : [...prev, tag],
    );
  }

  /** Adds a custom tag from the search query. Normalised + policy-checked. */
  function addCustomTag(raw: string) {
    setTagError(null);
    const normalised = raw.trim().toLowerCase().replace(/\s+/g, '-');
    if (!normalised) return;
    if (tags.length >= MAX_INTEREST_TAGS) {
      setTagError(`You can pick up to ${MAX_INTEREST_TAGS}. Remove one first.`);
      return;
    }
    if (tags.includes(normalised)) {
      setTagQuery('');
      return;
    }
    const policy = checkNicknamePolicy(normalised, { label: 'tag', minLength: 1, maxLength: 30 });
    if (!policy.ok) {
      setTagError(policy.reason);
      return;
    }
    setTags((prev) => [...prev, normalised]);
    setTagQuery('');
  }

  const communityName = me.eligibleCommunity?.communityName ?? 'your community';
  const allAcknowledged = checked.every(Boolean);

  function toggle(index: number) {
    setChecked((prev) => prev.map((value, i) => (i === index ? !value : value)));
  }

  async function onConsent() {
    setBusy(true);
    setError(null);
    try {
      // recordConsent also flips the user to verified_adult under the
      // community-trust model — see apps/api/src/routes/onboarding.ts.
      await recordConsent();
      setStep('nickname');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your consent.');
    } finally {
      setBusy(false);
    }
  }

  async function onNickname() {
    setBusy(true);
    setError(null);
    const result = await createMembership(nickname.trim());
    setBusy(false);
    if (result.ok) {
      setStep('interests');
    } else {
      setError(result.error);
    }
  }

  async function onInterests(skip: boolean) {
    setBusy(true);
    setError(null);
    if (!skip && tags.length > 0) {
      const result = await saveInterestTags(tags);
      if (!result.ok) {
        setBusy(false);
        setError(result.error);
        return;
      }
    }
    setBusy(false);
    setStep('checkin');
  }

  return (
    <main>
      <div className="card">
        {step === 'consent' && (
          <>
            <StepIndicator step={step} communityName={communityName} />
            <h1>First, a few quick promises.</h1>
            <p className="onboarding-lede">
              These keep everyone safe — yours, your match's, ours. Tap each to acknowledge, then
              we'll set up your nickname.
            </p>
            <div className="stack consent-stack">
              {CONSENT_POINTS.map((point, index) => (
                <label key={point} className="consent-item">
                  <input type="checkbox" checked={checked[index]} onChange={() => toggle(index)} />
                  <span>{point}</span>
                </label>
              ))}
              <button type="button" disabled={!allAcknowledged || busy} onClick={onConsent}>
                {busy ? 'Saving…' : 'I understand and agree'}
              </button>
              {error && <p className="error">{error}</p>}
            </div>
          </>
        )}

        {step === 'nickname' && (
          <>
            <StepIndicator step={step} communityName={communityName} />
            <h1>Pick a nickname.</h1>
            <p className="onboarding-lede">
              Your matches see this — not your name, not your email. Pick something you'd like to be
              greeted as, or tap a suggestion below.
            </p>
            <div className="stack">
              <label>
                Nickname
                <input
                  type="text"
                  value={nickname}
                  maxLength={24}
                  placeholder="e.g. RiverFox"
                  onChange={(event) => setNickname(event.target.value)}
                />
              </label>
              <div className="nickname-suggestions" aria-label="Nickname suggestions">
                {NICKNAME_SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="tag-chip" onClick={() => setNickname(s)}>
                    {s}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={nickname.trim().length < 2 || busy}
                onClick={onNickname}
              >
                {busy ? 'Joining…' : `Join ${communityName}`}
              </button>
              {error && <p className="error">{error}</p>}
            </div>
          </>
        )}

        {step === 'interests' &&
          (() => {
            const queryNorm = tagQuery.trim().toLowerCase().replace(/\s+/g, '-');
            // Selected chips always visible at the top (so the user sees
            // their current picks no matter what they're searching for).
            const selectedChips = tags;
            // Browse list = predefined tags minus already-selected, filtered
            // by query if any. We DON'T mix custom-but-unselected entries in
            // — there are none in the data model; custom tags only exist
            // once they're selected.
            const browseChips = INTEREST_TAGS.filter((t) => !tags.includes(t)).filter((t) =>
              queryNorm ? t.toLowerCase().includes(queryNorm) : true,
            );
            // Show "Add custom" affordance when the user typed something that
            // doesn't exactly match a predefined tag and isn't already in
            // their selection.
            const exactPresetMatch = (INTEREST_TAGS as readonly string[]).includes(queryNorm);
            const showAddCustom =
              queryNorm.length > 0 && !exactPresetMatch && !tags.includes(queryNorm);

            return (
              <>
                <StepIndicator step={step} communityName={communityName} />
                <h1>What lights you up?</h1>
                <p className="onboarding-lede">
                  Pick up to {MAX_INTEREST_TAGS} — we use these to weight matches toward shared
                  ground. Search the list, or type something we don't have.
                </p>

                {selectedChips.length > 0 && (
                  <div className="tag-grid" style={{ margin: '0.5rem 0 0.25rem' }}>
                    {selectedChips.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="tag-chip on"
                        aria-pressed="true"
                        onClick={() => toggleTag(tag)}
                      >
                        {tag.replace(/-/g, ' ')}
                        <span aria-hidden="true" style={{ marginLeft: '0.35rem', opacity: 0.6 }}>
                          ×
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="stack">
                  <label className="custom-tag-row">
                    <span className="muted">Search interests, or add your own</span>
                    <input
                      type="text"
                      value={tagQuery}
                      maxLength={30}
                      placeholder="e.g. dogs, plants, hiking, cycling…"
                      onChange={(event) => {
                        setTagQuery(event.target.value);
                        if (tagError) setTagError(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          if (showAddCustom) addCustomTag(tagQuery);
                        }
                      }}
                    />
                  </label>
                  {tagError && <p className="error">{tagError}</p>}
                </div>

                <div className="tag-grid tag-grid-scroll">
                  {browseChips.length === 0 && !showAddCustom && (
                    <p className="muted" style={{ width: '100%' }}>
                      Nothing matches “{tagQuery}”. Backspace to browse all.
                    </p>
                  )}
                  {browseChips.map((tag) => {
                    const atMax = tags.length >= MAX_INTEREST_TAGS;
                    return (
                      <button
                        key={tag}
                        type="button"
                        className="tag-chip"
                        disabled={atMax}
                        onClick={() => toggleTag(tag)}
                      >
                        {tag.replace(/-/g, ' ')}
                      </button>
                    );
                  })}
                  {showAddCustom && (
                    <button
                      type="button"
                      className="tag-chip tag-chip-add"
                      disabled={tags.length >= MAX_INTEREST_TAGS}
                      onClick={() => addCustomTag(tagQuery)}
                    >
                      + Add “{tagQuery.trim()}” as a new tag
                    </button>
                  )}
                </div>

                <div className="row">
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => void onInterests(true)}
                  >
                    Skip for now
                  </button>
                  <button type="button" disabled={busy} onClick={() => void onInterests(false)}>
                    {busy ? 'Saving…' : 'Continue'}
                  </button>
                </div>
                {error && <p className="error">{error}</p>}
              </>
            );
          })()}

        {step === 'checkin' && (
          <>
            <StepIndicator step={step} communityName={communityName} />
            <h1>One quick check-in.</h1>
            <p className="onboarding-lede">
              Three questions, totally private. Just a snapshot so the experience can be better over
              time — and so you can see your own progress. You can skip if you'd rather not.
            </p>
            <CheckinForm checkpoint="intake" onDone={onComplete} />
          </>
        )}
      </div>
    </main>
  );
}
